import { select } from "redux-saga/effects";

import { TripStatus } from "@mapmycustomers/shared";
import { Trip } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import { getDatesRangeAtOffset } from "@app/enum/dashboard/dateRanges";
import { callApi } from "@app/store/api/callApi";
import { getOrganizationId } from "@app/store/iam";
import { DefaultDateRangeConfiguration } from "@app/types/dashboard/CardConfiguration";
import Scope from "@app/types/dashboard/Scope";
import decodePolyline from "@app/util/map/decodePolyline";

// A single rep's day of trips stays well under this cap.
const TRIP_PAGE_SIZE = 500;

/**
 * Decodes an array of trips' polylines into arrays of {lat,lng} points.
 * Trips without a polyline or with fewer than 2 points are dropped.
 *
 * decodePolyline returns LongLat ([lng, lat]) pairs; we map to {lat, lng} for Google Maps.
 */
export const decodeTripPaths = (
  trips: Array<Pick<Trip, "polyline">>
): google.maps.LatLngLiteral[][] =>
  trips
    .filter((trip) => !!trip.polyline)
    .map((trip) => decodePolyline(trip.polyline!).map(([lng, lat]) => ({ lat, lng })))
    .filter((path) => path.length >= 2);

/**
 * Fetches all COMPLETED trips for the rep on the given span and returns
 * an array of decoded LatLng paths — one per trip.
 *
 * `spansBack` steps the window back by whole spans, matching the card's date span navigator.
 *
 * Rep-scope only: non-rep scopes (team or org-wide) return an empty array
 * because trip paths are personal and cannot be meaningfully aggregated.
 */
export function* fetchDayTripPaths(
  dateRange: DefaultDateRangeConfiguration,
  scope?: Scope,
  spansBack = 0
): Generator {
  if (!scope?.userId) {
    return [];
  }

  const orgId: Organization["id"] = yield select(getOrganizationId);
  // Must resolve to the same span the pins were fetched for, or the routes drawn on the map
  // would belong to a different day than the skipped stops sitting on top of them.
  const range = getDatesRangeAtOffset(dateRange.range, dateRange.subRange, spansBack);

  const response: ListResponse<Trip> = yield callApi("fetchTrips", orgId, {
    $columns: ["polyline", "startTime"],
    $filters: {
      $or: [
        { startTime: { $gte: range.startDate.toISOString(), $lte: range.endDate.toISOString() } },
      ],
      status: TripStatus.COMPLETED,
      userId: { $in: [scope.userId] },
    },
    $limit: TRIP_PAGE_SIZE,
    $order: "startTime",
  });

  return decodeTripPaths(response.data);
}
